import { createContext, useCallback, useContext, useRef, useState } from 'react';

// App-wide toast notifications. Wrap the app in <ToastProvider> once, then:
//   const toast = useToast();
//   toast.success('Vendor created');
//   toast.error('Failed to save');
//   toast.info('Invoice sent');
// Success/info dismiss after 4s, errors after 7s; all can be dismissed by hand.

const ToastContext = createContext(null);

const ICONS = {
  success: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  error: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  info: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

const KIND_COLOR = {
  success: 'var(--success)',
  error: 'var(--danger)',
  info: 'var(--info)',
};

const ToastItem = ({ toast, onDismiss }) => (
  <div
    role={toast.kind === 'error' ? 'alert' : 'status'}
    className="toast-item"
    style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      minWidth: '260px', maxWidth: '380px',
      padding: '12px 14px',
      background: 'var(--canvas)',
      border: '1px solid var(--hairline)',
      borderLeft: `3px solid ${KIND_COLOR[toast.kind]}`,
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-lg)',
      pointerEvents: 'auto',
    }}
  >
    <span style={{ color: KIND_COLOR[toast.kind], flexShrink: 0, display: 'flex', marginTop: '1px' }}>
      {ICONS[toast.kind]}
    </span>
    <div style={{
      flex: 1, minWidth: 0,
      fontFamily: 'var(--font-sans)', fontSize: '13.5px', fontWeight: 400,
      color: 'var(--ink)', lineHeight: 1.45,
      overflowWrap: 'break-word',
    }}>
      {toast.message}
    </div>
    <button
      onClick={() => onDismiss(toast.id)}
      aria-label="Dismiss notification"
      style={{
        background: 'transparent', border: 'none', padding: '2px',
        color: 'var(--muted-soft)', cursor: 'pointer', flexShrink: 0,
        display: 'flex', alignItems: 'center',
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </div>
);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
  }, []);

  const push = useCallback((kind, message) => {
    if (!message) return;
    const id = ++idRef.current;
    setToasts(prev => [...prev.slice(-3), { id, kind, message }]);
    const timer = setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4000);
    timersRef.current.set(id, timer);
  }, [dismiss]);

  const api = useRef({
    success: (msg) => push('success', msg),
    error: (msg) => push('error', msg),
    info: (msg) => push('info', msg),
  });
  // Keep the stable object pointing at the latest push.
  api.current.success = (msg) => push('success', msg);
  api.current.error = (msg) => push('error', msg);
  api.current.info = (msg) => push('info', msg);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed', top: '76px', right: '20px', zIndex: 4000,
          display: 'flex', flexDirection: 'column', gap: '10px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => <ToastItem key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Provider missing (e.g. isolated render); fail soft instead of crashing.
    return { success: () => {}, error: () => {}, info: () => {} };
  }
  return ctx;
};
